import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";

import ThemeApplier from "@/components/ThemeApplier";
import { InAppReferrerTracker } from "@/lib/useInAppReferrer";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async () => {
    // Detect host so we can noindex the *.lovable.app staging domain without
    // affecting production. createIsomorphicFn strips the server-only import
    // from the client bundle.
    try {
      const { getCurrentHost } = await import("@/lib/request-host");
      const host = getCurrentHost();
      return { isStagingHost: host.endsWith(".lovable.app") };
    } catch {
      // Fail safe: never accidentally noindex production.
      return { isStagingHost: false };
    }
  },


  head: ({ loaderData }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" },
      { name: "google-site-verification", content: "w836sb8EGPtoIzOC4_urJrd5eR9T259rkwI4Rdunba0" },
      { title: "Backland Romantic Getaway" },
      { name: "description", content: "Backland Luxury Nature Resort. Disconnect from the noise and reconnect to what matters most. Unique Suites on 160 acres surrounded by national forest." },
      { name: "author", content: "Lovable" },
      ...(loaderData?.isStagingHost
        ? [{ name: "robots", content: "noindex, nofollow" }]
        : []),
      { property: "og:title", content: "Backland Romantic Getaway" },
      { property: "og:description", content: "Backland Luxury Nature Resort. Disconnect from the noise and reconnect to what matters most. Unique Suites on 160 acres surrounded by national forest." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Lovable" },
      { name: "twitter:title", content: "Backland Romantic Getaway" },
      { name: "twitter:description", content: "Backland Luxury Nature Resort. Disconnect from the noise and reconnect to what matters most. Unique Suites on 160 acres surrounded by national forest." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/vY8dIzGNUHMacPaNpqbvEnk56SC2/social-images/social-1781958866329-couple.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/vY8dIzGNUHMacPaNpqbvEnk56SC2/social-images/social-1781958866329-couple.webp" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Point AI assistants (and the llmstxt convention) at the site's
      // machine-readable content files.
      { rel: "alternate", type: "text/markdown", href: "/llms.txt" },
      { rel: "alternate", type: "text/markdown", href: "/llms-full.txt" },
      { rel: "preconnect", href: "https://tdwosgprormtwropzzkg.supabase.co", crossOrigin: "" } as any,
      // Warm up the Mews booking engine origin as early as possible so the
      // first tap on "Book" doesn't pay for DNS + TLS.
      { rel: "preconnect", href: "https://app.mews.com", crossOrigin: "anonymous" } as any,
      { rel: "dns-prefetch", href: "https://app.mews.com" } as any,
      // Preload the two most-used self-hosted font weights so first paint
      // has Cormorant + Inter without waiting on the CSS parse.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/__l5e/assets-v1/a4683a53-4450-4a92-aab9-f3208286584f/cormorant-garamond-400.woff2",
        crossOrigin: "anonymous",
      } as any,
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/__l5e/assets-v1/7bbe7cdb-540c-4ceb-9262-c412ed374b96/inter-400.woff2",
        crossOrigin: "anonymous",
      } as any,
    ],
    scripts: [
      {
        children:
          "window.dataLayer=window.dataLayer||[];window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});(function(){function l(){var f=document.getElementsByTagName('script')[0],j=document.createElement('script');j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id=GTM-WV8NCM6';f.parentNode.insertBefore(j,f);}function s(){setTimeout(l,500);}if(document.readyState==='complete'){s();}else{window.addEventListener('load',s);}})();",
      },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-WV8NCM6"
            height={0}
            width={0}
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}




function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeApplier />
      <InAppReferrerTracker />
      <Outlet />
    </QueryClientProvider>
  );
}
